import {
  isInterrupt,
  isLethal,
  isPowerless,
  isRescue,
  type CardDef,
} from '../../../../core/card';
import type { CardTitle } from '../../../../core/ids';
import type { FaceSpec } from '../spec';
import { breakFace } from './break';
import { docketFace } from './docket';
import { exceptionFace } from './exception';
import { insertFace } from './insert';
import { placeholderFace } from './placeholder';
import { returnFace } from './return';

/**
 * **牌面分派。**
 *
 * ⚠️ 按 `traits` 分派，**绝不按牌名**。
 *
 * 这条规矩是整套可扩展设计的地基：核心逻辑只认 `traits`，画法也一样认
 * `traits`。于是扩展包塞进来一张"内爆猫"时，它声明 `lethal` 就自动拿到
 * 白卡原型、自动拿到危险色、自动接上印刷层 —— **这个文件一行都不用改**。
 *
 * 分派顺序和 `docs/card-faces.md` §3 的原型表、以及 `placeholder.accentFor()`
 * 完全一致，实现时复用同一个顺序，不要各写一遍。
 */
export function faceFor(card: CardDef): FaceSpec {
  if (isLethal(card)) return exceptionFace(card);
  if (isRescue(card)) return returnFace(card);
  if (isInterrupt(card)) return breakFace(card);
  // 无效牌 → 夹页（素人手绘 + 勒索信牌名）。⚠️ 它们**不出框**
  if (isPowerless(card)) return insertFace(card);
  // ⚠️ 剩下的都走单据，但**只有画过的五张**才有图 ——
  //    扩展包塞进来的新主动牌会拿到"有框没图"的版式，而不是一片空白。
  //    等它自己配了图，这里一行都不用改
  return DOCKETS.has(card.local) ? docketFace(card) : placeholderFace(card);
}

/** 已经画好图的那五张单据 */
const DOCKETS: ReadonlySet<string> = new Set(['Signal', 'Suspend', 'Await', 'Hash', 'Debug']);

/**
 * **这张牌的辨识色** —— 和它牌面页眉 / 语义色竖条用的那管墨**同一个值**
 * （就是 `faceFor(card).accent`）。
 *
 * UI 拿它给「被攻击的额外回合」按钮上色：按钮颜色因此永远等于来源牌的颜色，
 * 扩展包加一张别的颜色的攻击牌、或者换掉某张牌的画法，界面一行都不用改。
 *
 * ⚠️ 结果按牌名缓存：`faceFor` 会算整份牌面几何（网点、手绘噪声），
 *    而配色是个静态量，没必要每帧重算。
 */
const ACCENTS = new Map<CardTitle, number>();

export function cardAccent(card: CardDef): number {
  const hit = ACCENTS.get(card.id);
  if (hit !== undefined) return hit;
  const accent = faceFor(card).accent;
  ACCENTS.set(card.id, accent);
  return accent;
}

/**
 * 画法内容的版本号。
 *
 * ⚠️ 改了 `faces/` 里的几何就 +1。牌面缓存按
 * `牌名 × 尺寸 × 分辨率 × 内容版本` 缓存，版本不动它就不会重画。
 */
export const FACE_VERSION = 4;
